import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { TableProps } from 'antd';
import { usePageLoading } from '@/hooks/usePageLoading';
import { useGetStudiesListQuery } from '@/state/slices/studies';
import { useGetUsersQuery } from '@/state/slices/users';
import { useGetLogItemsQuery } from '@/state/slices/logItem';
import { userSorterToOrdering } from 'services/utils';
import { UserParams } from '@/models/user';
import { LogItem } from '@/models/logItem';
import { IUser } from '@/features/admin/UserTable';
import { useAdminStudyFilters } from '@/features/admin/AdminStudyFilterRail';
import { useAuditFilters } from '@/features/admin/AuditFilterRail';

export type AdminTab =
  | 'studies'
  | 'users'
  | 'reporting-settings'
  | 'integrations'
  | 'feature-flags'
  | 'audit';

const pathToTab: Record<string, AdminTab> = {
  '/admin/studies': 'studies',
  '/admin/users': 'users',
  '/admin/reporting-settings': 'reporting-settings',
  '/admin/integrations': 'integrations',
  '/admin/feature-flags': 'feature-flags',
  '/admin/audit': 'audit',
};

export function useAdminState() {
  const navigate = useNavigate();
  const location = useLocation();

  // ---- Tab routing ----
  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      navigate('/admin/studies', { replace: true });
    }
  }, [location.pathname, navigate]);

  const activeTab: AdminTab = pathToTab[location.pathname] || 'studies';

  const onTabChange = (value: AdminTab) => {
    navigate(`/admin/${value}`);
  };

  // ---- Studies state ----
  const studyFilters = useAdminStudyFilters();

  const {
    data: studiesData,
    isFetching: isFetchingStudies,
    isLoading: isLoadingStudies,
  } = useGetStudiesListQuery(studyFilters.params);

  // ---- Users state ----
  const [userParams, setUserParams] = useState<UserParams>({
    page: 1,
    size: 10,
  });
  const [searchUserText, setSearchUserText] = useState('');

  const {
    data: usersData,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetUsersQuery(userParams);

  useEffect(() => {
    const id = setTimeout(() => {
      setUserParams((p) => ({
        ...p,
        page: 1,
        q: searchUserText || undefined,
      }));
    }, 350);
    return () => clearTimeout(id);
  }, [searchUserText]);

  const onUserTableChange: TableProps<IUser>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    setUserParams((p) => ({
      ...p,
      page: pagination?.current ?? p.page,
      size: pagination?.pageSize ?? p.size,
      ordering: userSorterToOrdering(sorter),
    }));
  };

  const onRoleChange = (val: 'All' | IUser['role']) => {
    setUserParams((p) => ({
      ...p,
      page: 1,
      roles: val === 'All' ? undefined : val.toUpperCase(),
    }));
  };

  const onStatusChange = (val: 'All' | IUser['status']) => {
    setUserParams((p) => ({
      ...p,
      page: 1,
      statuses: val === 'All' ? undefined : val,
    }));
  };

  // ---- Audit state ----
  const auditFilters = useAuditFilters();
  const [selectedLogItem, setSelectedLogItem] = useState<LogItem | null>(null);

  const {
    data: logItemsData,
    isFetching: isFetchingLogItems,
    isLoading: isLoadingLogItems,
  } = useGetLogItemsQuery(auditFilters.params);

  // ---- Loading ----
  const isInitialLoading =
    isLoadingStudies || isLoadingUsers || isLoadingLogItems;
  usePageLoading(isInitialLoading);

  return {
    activeTab,
    onTabChange,

    // Studies
    studiesData,
    isFetchingStudies,
    studyFilters,

    // Users
    usersData,
    isFetchingUsers,
    userParams,
    searchUserText,
    setSearchUserText,
    onUserTableChange,
    onRoleChange,
    onStatusChange,

    // Audit
    auditFilters,
    logItemsData,
    isFetchingLogItems,
    selectedLogItem,
    setSelectedLogItem,
  };
}
